import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays, Clock, ArrowRight, MessageCircle, Loader2,
  CheckCircle2, Lock, Building2, Sparkles,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ordersApi } from '@/services/api';
import { useSettings } from '@/hooks/useSettings';
import { toast } from 'sonner';
import OrderLayout from './OrderLayout';
import { useOrderState } from './useOrderState';
import DatePicker from '@/components/DatePicker';
import { useLanguage } from '@/context/LanguageContext';

const TIME_SLOTS = ['09:00', '11:00', '13:00', '15:00', '17:00'];

const TIME_LABELS = {
  '09:00': { en: '9:00 AM', ar: '٩:٠٠ صباحاً', period_en: 'Morning', period_ar: 'صباح' },
  '11:00': { en: '11:00 AM', ar: '١١:٠٠ صباحاً', period_en: 'Late Morning', period_ar: 'ضحى' },
  '13:00': { en: '1:00 PM', ar: '١:٠٠ ظهراً', period_en: 'Noon', period_ar: 'ظهر' },
  '15:00': { en: '3:00 PM', ar: '٣:٠٠ عصراً', period_en: 'Afternoon', period_ar: 'عصر' },
  '17:00': { en: '5:00 PM', ar: '٥:٠٠ مساءً', period_en: 'Evening', period_ar: 'مساء' },
};

const inputCls = 'w-full px-4 py-3 border border-charcoal/15 bg-white/70 text-charcoal focus:outline-none focus:border-gold transition-colors resize-none';

const ScheduleVisitPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLang } = useLanguage();
  const isAr = currentLang === 'ar';
  const { order, localData, loading } = useOrderState();
  const { settings } = useSettings();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ date: '', time_slot: '', notes: '' });

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (!loading && !localData) navigate('/start');
  }, [loading, localData, navigate]);

  if (loading) {
    return (
      <OrderLayout currentStep={2}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-gold" size={32} />
        </div>
      </OrderLayout>
    );
  }

  const isLocked = order && order.current_step > 2;
  const booked = order?.technician_visit;
  const adminPhone = (settings?.whatsapp || '').replace(/[^\d+]/g, '');

  const buildWaLink = (msg) => `https://wa.me/${adminPhone}?text=${encodeURIComponent(msg)}`;

  const changeRequestMsg = () => {
    const lines = [
      `مرحباً فريق أرمان كيتشنز 👋`,
      `أرغب في تعديل موعد زيارة الفني`,
      `اسم العميل: ${localData?.customerName || ''}`,
      `رقم الطلب: ${localData?.orderNumber || ''}`,
      localData?.accessPassword ? `رمز التحقق: ${localData.accessPassword}` : '',
    ].filter(Boolean);
    return lines.join('\n');
  };

  const confirmationMsg = () => {
    const lines = [
      `مرحباً فريق أرمان كيتشنز 👋`,
      `اسم العميل: ${localData?.customerName || ''}`,
      `رقم الطلب: ${localData?.orderNumber || ''}`,
      localData?.accessPassword ? `رمز التحقق: ${localData.accessPassword}` : '',
      ``,
      `تم حجز موعد زيارة فني المقاسات:`,
      `📅 التاريخ: ${form.date}`,
      `⏰ الوقت: ${TIME_LABELS[form.time_slot]?.[isAr ? 'ar' : 'en'] || form.time_slot}`,
      form.notes ? `📝 ملاحظات: ${form.notes}` : '',
      ``,
      `يرجى التأكيد. شكراً 🙏`,
    ].filter((l) => l !== null);
    return lines.join('\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time_slot) {
      toast.error(t('order.form.selectDateTime'));
      return;
    }
    setSubmitting(true);
    try {
      await ordersApi.updateStep(localData.orderId, 2, form);
      toast.success(t('order.saved'));
      setDone(true);
    } catch {
      toast.error(t('order.error'));
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Locked (already booked) view ── */
  if (isLocked && booked?.date) {
    return (
      <OrderLayout currentStep={2} orderNumber={localData?.orderNumber}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="flex items-center gap-3 p-5 bg-green-50 border border-green-200">
            <CheckCircle2 className="text-green-500 flex-shrink-0" size={24} />
            <div>
              <p className="font-bold text-charcoal">{t('order.locked.booked')}</p>
              <p className="text-charcoal/60 text-sm">
                {isAr ? `${booked.date} — ${TIME_LABELS[booked.time_slot]?.ar || booked.time_slot}` : `${booked.date} — ${TIME_LABELS[booked.time_slot]?.en || booked.time_slot}`}
              </p>
            </div>
            <Lock size={16} className="text-charcoal/30 ms-auto flex-shrink-0" />
          </div>

          <a
            href={buildWaLink(changeRequestMsg())}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 border-2 border-[#25D366]/40 text-charcoal font-semibold py-3 hover:border-[#25D366] hover:bg-[#25D366]/5 transition-all"
          >
            <MessageCircle size={18} className="text-[#25D366]" />
            {t('order.locked.requestChange')}
          </a>

          <button
            onClick={() => navigate('/showroom')}
            className="w-full bg-gold text-charcoal-deep font-bold py-4 flex items-center justify-center gap-3 hover:opacity-90 transition-opacity"
          >
            {t('order.steps.showroom.title')} <ArrowRight size={20} />
          </button>
        </motion.div>
      </OrderLayout>
    );
  }

  /* ── After submit success: WhatsApp + animated next step ── */
  if (done) {
    return (
      <OrderLayout currentStep={2} orderNumber={localData?.orderNumber}>
        <div className="space-y-6">
          {/* Success */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-4 space-y-4"
          >
            <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto">
              <MessageCircle className="text-gold" size={30} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-charcoal mb-1">{t('order.whatsapp.title')}</h3>
              <p className="text-charcoal/60 text-sm">{t('order.whatsapp.desc')}</p>
            </div>
            <a
              href={buildWaLink(confirmationMsg())}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] text-white font-bold px-8 py-3.5 hover:opacity-90 transition-opacity shadow-lg shadow-[#25D366]/20"
            >
              <MessageCircle size={20} />
              {t('order.whatsapp.cta')}
            </a>
          </motion.div>

          {/* Animated next step card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="relative overflow-hidden border-2 border-gold/20 bg-gradient-to-br from-gold/5 to-gold/10 p-6"
          >
            <div className="absolute -top-4 -end-4 w-24 h-24 rounded-full bg-gold/8" />
            <div className="absolute -bottom-6 -start-6 w-32 h-32 rounded-full bg-gold/5" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gold/15 rounded-full flex items-center justify-center">
                  <Building2 size={20} className="text-gold" />
                </div>
                <Sparkles size={16} className="text-gold/60" />
              </div>
              <h3 className="text-lg font-bold text-charcoal mb-1">{t('order.showroomNext.title')}</h3>
              <p className="text-charcoal/60 text-sm mb-4">{t('order.showroomNext.desc')}</p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                animate={{ boxShadow: ['0 0 0 0 rgba(239,127,26,0)', '0 0 0 8px rgba(239,127,26,0.15)', '0 0 0 0 rgba(239,127,26,0)'] }}
                transition={{ repeat: Infinity, duration: 2, delay: 0.8 }}
                onClick={() => navigate('/showroom')}
                className="flex items-center gap-2 bg-gold text-charcoal-deep font-bold px-6 py-3 hover:opacity-90 transition-opacity"
              >
                {t('order.showroomNext.cta')} <ArrowRight size={18} />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </OrderLayout>
    );
  }

  /* ── Booking form ── */
  return (
    <OrderLayout currentStep={2} orderNumber={localData?.orderNumber}>
      <form onSubmit={handleSubmit} className="space-y-7">
        {/* Calendar */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-charcoal/70 mb-3">
            <CalendarDays size={14} />
            {t('order.form.date')} <span className="text-gold">*</span>
          </label>
          <DatePicker
            value={form.date}
            onChange={(d) => setForm((p) => ({ ...p, date: d }))}
            minDate={today}
          />
        </div>

        {/* Time slots */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-charcoal/70 mb-3">
            <Clock size={14} />
            {t('order.form.timeSlot')} <span className="text-gold">*</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {TIME_SLOTS.map((slot) => {
              const label = TIME_LABELS[slot];
              const isActive = form.time_slot === slot;
              return (
                <button
                  key={slot}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, time_slot: slot }))}
                  className={`py-3 px-2 text-center border-2 transition-all duration-200 ${
                    isActive
                      ? 'border-gold bg-gold shadow-md shadow-gold/20'
                      : 'border-charcoal/12 bg-white/60 hover:border-gold/40 hover:bg-gold/5'
                  }`}
                >
                  <p className={`text-sm font-bold ${isActive ? 'text-charcoal-deep' : 'text-charcoal'}`}>
                    {label?.[isAr ? 'ar' : 'en']}
                  </p>
                  <p className={`text-xs mt-0.5 ${isActive ? 'text-charcoal-deep/70' : 'text-charcoal/40'}`}>
                    {label?.[isAr ? 'period_ar' : 'period_en']}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-charcoal/70 mb-2">{t('order.form.notes')}</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            rows={3}
            className={inputCls}
            placeholder={t('order.form.notes')}
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-gold text-charcoal-deep font-bold py-4 flex items-center justify-center gap-3 hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {submitting ? (
            <><Loader2 size={18} className="animate-spin" /> {t('order.form.saving')}</>
          ) : (
            <>{t('order.form.submit')} <ArrowRight size={20} /></>
          )}
        </button>
      </form>
    </OrderLayout>
  );
};

export default ScheduleVisitPage;
